import React, { useEffect, useState, useRef, useCallback } from 'react';
import AuthComponent from './components/AuthComponent';
import './JobLandingPage.scss';
import JobDetailsComponent from './components/JobDetailsComponent';
import { getName, getUserObject } from '../../legacy_components/util/getUserFullName';
import PropTypes from 'prop-types';
import MobileScrollNotification from './components/MobileScrollNotification';

const JobLandingPage = ({ job = {}, checkAccountExistsStatus, onContinue, btnLoading }) => {
    const [authenticationData, setAuthenticationData] = useState({});
    const [showMobileNotification, setShowMobileNotification] = useState(false);
    const authRef = useRef(null);
    const jobInfoRef = useRef(null);

    // Utility to update authentication data state
    const updateStateAuthData = useCallback(() => {
        const userObject = getUserObject() || {};
        const name = getName();
        if (!userObject.name) userObject.name = name;
        if (userObject.uid) setAuthenticationData(userObject);
    }, []);

    // Utility to handle smooth scroll to top
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Synchronize authentication data on mount
    useEffect(() => {
        updateStateAuthData();
    }, [updateStateAuthData]);

    // Observer to toggle mobile notification based on intersection
    useEffect(() => {
        const authContainer = authRef.current?.querySelector('.auth-page__left-container');
        if (!authContainer) return;

        const observer = new IntersectionObserver(
            ([entry]) => setShowMobileNotification(!entry.isIntersecting),
            { root: null, threshold: 0 }
        );

        observer.observe(authContainer);
        return () => observer.disconnect();
    }, []);

    // Dynamically adjust heights based on `.auth-wrapper` changes
    useEffect(() => {
        const adjustHeights = () => {
            const authWrapper = authRef.current?.querySelector('.job-landing-page__auth-wrapper');
            if (authWrapper) {
                const authWrapperHeight = authWrapper.offsetHeight;
                if (jobInfoRef.current) {
                    jobInfoRef.current.style.minHeight = `${authWrapperHeight}px`;
                }

                const jobPageCard = jobInfoRef.current?.querySelector('.job-page-card');
                if (jobPageCard) {
                    jobPageCard.style.minHeight = `${authWrapperHeight}px`;
                }
            }
        };

        const resizeObserver = new ResizeObserver(adjustHeights);
        if (authRef.current) resizeObserver.observe(authRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div className="job-landing-page">
            {/* Authentication Section */}
            <div className="job-landing-page__auth" ref={authRef}>
                <AuthComponent
                    btnLoading={btnLoading}
                    setAuthenticationData={setAuthenticationData}
                    authenticationData={authenticationData}
                    updateStateAuthData={updateStateAuthData}
                    onContinue={onContinue}
                    checkAccountExistsStatus={checkAccountExistsStatus}
                />
            </div>

            {/* Job Details Section */}
            <div className="job-landing-page__job-info" ref={jobInfoRef}>
                <JobDetailsComponent job={job} />
            </div>

            {/* Mobile Scroll Notification */}
            {showMobileNotification && (
                <MobileScrollNotification
                    auth={authenticationData.uid}
                    job_title={job.job_title}
                    btnLoading={btnLoading}
                    onContinue={authenticationData.uid ? onContinue : scrollToTop}
                />
            )}
        </div>
    );
};

JobLandingPage.propTypes = {
    job: PropTypes.object,
    checkAccountExistsStatus: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    btnLoading: PropTypes.bool.isRequired,
};

export default JobLandingPage;
